import { BusinessIDType, EntityType } from 'compliance-api/generated';
import { useFormikContext } from 'formik';
import { countryOptions } from '@/modules/common/constants/countries.ts';
import {
  businessNumberTypeOptions,
  entityTypeOptions,
} from '@/modules/admin-dashboard/components/contractManagement/partyOptions.ts';
import { Button, Select, TextInputField } from 'ui';

export interface PartyDetailsFormValues {
  legal_name: string;
  trading_name: string;
  business_number: string;
  business_number_type: BusinessIDType;
  entity_type: EntityType | null;
  incorporation_country: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postcode: string;
  is_active: boolean;
}

export const PartyDetailsTab = ({
  showSaveButton,
  readOnly = false,
}: {
  showSaveButton: boolean;
  readOnly?: boolean;
}) => {
  const formik = useFormikContext<PartyDetailsFormValues>();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
        <TextInputField name="legal_name" label="Legal name" disabled={readOnly} />
        <TextInputField name="trading_name" label="Trading name" disabled={readOnly} />

        <Select<BusinessIDType>
          label="Business identifier"
          options={businessNumberTypeOptions}
          value={formik.values.business_number_type}
          onChange={value => formik.setFieldValue('business_number_type', value)}
          rounded="right"
          disabled={readOnly}
        />
        <TextInputField
          name="business_number"
          label="Business number"
          placeholder="Number"
          disabled={readOnly}
        />

        <Select<EntityType, false, true>
          label="Entity type"
          options={entityTypeOptions}
          value={formik.values.entity_type}
          onChange={value => formik.setFieldValue('entity_type', value)}
          isClearable
          disabled={readOnly}
        />
        <Select<string, false, true>
          label="Incorporation country"
          options={
            formik.values.incorporation_country &&
            !countryOptions.some(option => option.value === formik.values.incorporation_country)
              ? [
                  {
                    value: formik.values.incorporation_country,
                    label: formik.values.incorporation_country,
                  },
                  ...countryOptions,
                ]
              : countryOptions
          }
          value={formik.values.incorporation_country || null}
          onChange={value => formik.setFieldValue('incorporation_country', value || '')}
          isClearable
          disabled={readOnly}
        />

        <TextInputField name="phone" label="Phone number" disabled={readOnly} />
        <TextInputField name="address_line1" label="Address line 1" disabled={readOnly} />

        <TextInputField name="address_line2" label="Address line 2" disabled={readOnly} />
        <TextInputField name="city" label="City" disabled={readOnly} />

        <TextInputField name="state" label="State / Province" disabled={readOnly} />
        <TextInputField name="postcode" label="Postcode" disabled={readOnly} />
      </div>

      {showSaveButton && formik.dirty && !readOnly && (
        <div className="flex justify-end">
          <Button
            className="h-8 min-h-[32px] w-fit min-w-0 px-4 py-1 text-xs"
            onClick={formik.submitForm}
            disabled={formik.isSubmitting}
            loading={formik.isSubmitting}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};
